import React from 'react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { AppSelect } from '../../../components/AppSelect';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../utils/settingsSectionStyles';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import {
  DEFAULT_SKONTO_DAYS,
  formatPaymentTermsSentence,
  IMMEDIATE_DUE_WORKING_DAYS,
  normalizePaymentTermsDays,
  normalizePaymentTermsMode,
  type PaymentTermsMode,
} from '../../../utils/paymentTerms';

const fieldLabel = SETTINGS_FIELD_LABEL;
const inputClass = `${SETTINGS_INPUT_CLASS} bg-app-surface`;
const btnOutline = SETTINGS_BTN_OUTLINE;
const btnPrimary = SETTINGS_BTN_PRIMARY;
const selectTriggerClass = SETTINGS_SELECT_TRIGGER_CLASS;

const PRESET_DAYS = [7, 14, 30] as const;

const ZAHLUNGSZIEL_OPTIONS = [
  { value: 'immediate', label: 'Sofort' },
  { value: '7', label: '7 Tage' },
  { value: '14', label: '14 Tage' },
  { value: '30', label: '30 Tage' },
  { value: 'custom', label: 'Benutzerdefiniert' },
];

type PaymentSelectValue = 'immediate' | '7' | '14' | '30' | 'custom';

type PaymentForm = {
  payment_terms_mode: PaymentTermsMode;
  payment_terms_days: number;
  skonto_percent: number | null;
  skonto_days: number | null;
};

function selectValueFromForm(mode: PaymentTermsMode, days: number): PaymentSelectValue {
  if (mode === 'immediate') return 'immediate';
  if ((PRESET_DAYS as readonly number[]).includes(days)) return String(days) as '7' | '14' | '30';
  return 'custom';
}

function formatPaymentTermsDisplay(mode: PaymentTermsMode, days: number) {
  if (mode === 'immediate') return `Sofort (fällig in ${IMMEDIATE_DUE_WORKING_DAYS} Werktagen)`;
  return days === 1 ? '1 Tag' : `${days} Tage`;
}

function formatSkontoDisplay(p: number | null, days: number | null) {
  if (p == null) return 'Kein Skonto';
  const d = days != null && days >= 1 ? days : DEFAULT_SKONTO_DAYS;
  return `${String(p).replace('.', ',')} % / ${d} ${d === 1 ? 'Tag' : 'Tage'}`;
}

/** The Skonto field is plain text (Safari drops "2," from number inputs), so show a comma. */
function skontoDraftFromPercent(p: number): string {
  return String(p).replace('.', ',');
}

function parseSkontoDays(raw: unknown): number | null {
  const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? '').trim(), 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.min(9999, Math.floor(n));
}

function paymentFormDirty(
  form: PaymentForm,
  snapshot: PaymentForm,
  paymentTermsDraft: string,
  skontoDraft: string,
  skontoDaysDraft: string,
  customSelected: boolean,
): boolean {
  if (form.payment_terms_mode !== snapshot.payment_terms_mode) return true;
  if (form.payment_terms_mode === 'net_days') {
    const days = customSelected
      ? normalizePaymentTermsDays(paymentTermsDraft || form.payment_terms_days)
      : form.payment_terms_days;
    if (days !== snapshot.payment_terms_days) return true;
    const wasCustom = selectValueFromForm(snapshot.payment_terms_mode, snapshot.payment_terms_days) === 'custom';
    if (customSelected !== wasCustom) return true;
  }
  const skontoOn = form.payment_terms_mode !== 'immediate' && form.skonto_percent != null;
  const snapSkontoOn = snapshot.payment_terms_mode !== 'immediate' && snapshot.skonto_percent != null;
  if (skontoOn !== snapSkontoOn) return true;
  if (skontoOn) {
    const n = parseFloat(skontoDraft.replace(',', '.'));
    const pct = Number.isFinite(n) && n > 0 ? Math.min(100, Math.round(n * 100) / 100) : form.skonto_percent;
    if (pct !== snapshot.skonto_percent) return true;
    const days = parseSkontoDays(skontoDaysDraft) ?? form.skonto_days ?? DEFAULT_SKONTO_DAYS;
    if (days !== (snapshot.skonto_days ?? DEFAULT_SKONTO_DAYS)) return true;
  }
  return false;
}

function paymentFromApi(data: {
  payment_terms_days?: unknown;
  payment_terms_mode?: unknown;
  skonto_percent?: number | null;
  skonto_days?: number | null;
}): PaymentForm {
  const sk = data.skonto_percent;
  const percent =
    sk != null && typeof sk === 'number' && Number.isFinite(sk) && sk > 0
      ? Math.round(sk * 100) / 100
      : null;
  return {
    payment_terms_mode: normalizePaymentTermsMode(data.payment_terms_mode),
    payment_terms_days: normalizePaymentTermsDays(data.payment_terms_days),
    skonto_percent: percent,
    skonto_days: percent != null ? parseSkontoDays(data.skonto_days) ?? DEFAULT_SKONTO_DAYS : null,
  };
}

/** showOffers / showInvoices: Vorschauen nur für gebuchte Module (Angebote, Rechnungen). */
export function PaymentConditionsSettingsSection({
  showOffers = true,
  showInvoices = true,
}: { showOffers?: boolean; showInvoices?: boolean } = {}) {
  const [form, setForm] = React.useState<PaymentForm>({
    payment_terms_mode: 'net_days',
    payment_terms_days: 7,
    skonto_percent: null,
    skonto_days: null,
  });
  const [paymentTermsDraft, setPaymentTermsDraft] = React.useState('');
  const [skontoDraft, setSkontoDraft] = React.useState('');
  const [skontoDaysDraft, setSkontoDaysDraft] = React.useState('');
  const [customSelected, setCustomSelected] = React.useState(false);
  const [editSnapshot, setEditSnapshot] = React.useState<PaymentForm | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const editing = editSnapshot !== null;
  const dirty =
    editing &&
    editSnapshot != null &&
    paymentFormDirty(form, editSnapshot, paymentTermsDraft, skontoDraft, skontoDaysDraft, customSelected);
  const selectValue: PaymentSelectValue =
    form.payment_terms_mode === 'immediate'
      ? 'immediate'
      : customSelected
        ? 'custom'
        : selectValueFromForm(form.payment_terms_mode, form.payment_terms_days);
  const previewDays =
    editing && selectValue === 'custom'
      ? normalizePaymentTermsDays(paymentTermsDraft || form.payment_terms_days)
      : form.payment_terms_days;
  const previewSkontoPercent =
    form.payment_terms_mode === 'immediate' || form.skonto_percent == null
      ? null
      : editing
        ? (() => {
            const n = parseFloat(skontoDraft.replace(',', '.'));
            return Number.isFinite(n) && n > 0 ? n : form.skonto_percent;
          })()
        : form.skonto_percent;
  const previewSkontoDays =
    previewSkontoPercent == null
      ? null
      : editing
        ? parseSkontoDays(skontoDaysDraft) ?? form.skonto_days ?? DEFAULT_SKONTO_DAYS
        : form.skonto_days;
  const invoicePreview = formatPaymentTermsSentence({
    mode: form.payment_terms_mode,
    days: previewDays,
    context: 'invoice',
    skontoPercent: previewSkontoPercent,
    skontoDays: previewSkontoDays,
  });
  const offerPreview = formatPaymentTermsSentence({
    mode: form.payment_terms_mode,
    days: previewDays,
    context: 'offer',
    skontoPercent: previewSkontoPercent,
    skontoDays: previewSkontoDays,
  });

  React.useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setLoadError(null);
    fetch('/api/settings/company', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error || 'Zahlungskonditionen konnten nicht geladen werden.');
        }
        return res.json() as Promise<{
          payment_terms_days?: unknown;
          payment_terms_mode?: unknown;
          skonto_percent?: number | null;
          skonto_days?: number | null;
        }>;
      })
      .then((data) => {
        if (cancelled) return;
        setForm(paymentFromApi(data));
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Zahlungskonditionen konnten nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const beginEdit = () => {
    setEditSnapshot({ ...form });
    setPaymentTermsDraft(String(form.payment_terms_days));
    setSkontoDraft(form.skonto_percent != null ? skontoDraftFromPercent(form.skonto_percent) : '');
    setSkontoDaysDraft(form.skonto_days != null ? String(form.skonto_days) : String(DEFAULT_SKONTO_DAYS));
    setCustomSelected(selectValueFromForm(form.payment_terms_mode, form.payment_terms_days) === 'custom');
    setError(null);
    setSaved(false);
  };

  const cancelEdit = () => {
    if (editSnapshot) setForm(editSnapshot);
    setEditSnapshot(null);
    setPaymentTermsDraft('');
    setSkontoDraft('');
    setSkontoDaysDraft('');
    setCustomSelected(false);
    setError(null);
  };

  const onSelectChange = (value: string) => {
    if (value === 'immediate') {
      setCustomSelected(false);
      setForm((f) => ({ ...f, payment_terms_mode: 'immediate' }));
      return;
    }
    if (value === 'custom') {
      setCustomSelected(true);
      setForm((f) => ({ ...f, payment_terms_mode: 'net_days' }));
      setPaymentTermsDraft((prev) => prev || String(form.payment_terms_days));
      return;
    }
    const days = Number(value);
    if ((PRESET_DAYS as readonly number[]).includes(days)) {
      setCustomSelected(false);
      setForm((f) => ({ ...f, payment_terms_mode: 'net_days', payment_terms_days: days }));
      setPaymentTermsDraft(String(days));
    }
  };

  const save = async () => {
    const mode = form.payment_terms_mode;
    let paymentTermsDays = form.payment_terms_days;
    if (mode === 'net_days') {
      const trimmed = paymentTermsDraft.trim() || String(form.payment_terms_days);
      const parsedDays = trimmed ? parseInt(trimmed, 10) : NaN;
      if (!Number.isFinite(parsedDays) || parsedDays < 1) {
        setError('Bitte ein Zahlungsziel von mindestens 1 Tag eingeben.');
        return;
      }
      paymentTermsDays = Math.min(9999, parsedDays);
    }

    let skontoPercent: number | null = null;
    let skontoDays: number | null = null;
    if (mode !== 'immediate' && form.skonto_percent != null) {
      const skontoTrimmed = skontoDraft.trim();
      const parsedSkonto = skontoTrimmed ? parseFloat(skontoTrimmed.replace(',', '.')) : NaN;
      if (!Number.isFinite(parsedSkonto) || parsedSkonto <= 0) {
        setError('Bitte einen Skonto-Wert größer als 0 eingeben.');
        return;
      }
      skontoPercent = Math.min(100, Math.round(parsedSkonto * 100) / 100);
      skontoDays = parseSkontoDays(skontoDaysDraft) ?? DEFAULT_SKONTO_DAYS;
    } else if (mode === 'immediate') {
      skontoPercent = form.skonto_percent;
      skontoDays = form.skonto_days;
    }

    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch('/api/settings/company', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          payment_terms_mode: mode,
          payment_terms_days: paymentTermsDays,
          skonto_percent: skontoPercent,
          skonto_days: skontoDays,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        payment_terms_days?: unknown;
        payment_terms_mode?: unknown;
        skonto_percent?: number | null;
        skonto_days?: number | null;
      };
      if (!res.ok) {
        setError(data.error || 'Speichern fehlgeschlagen.');
        return;
      }
      setForm(paymentFromApi(data));
      setEditSnapshot(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setError('Netzwerkfehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <SettingsCard title="Zahlungsziel & Skonto">
        <InlineLoadingState label="Lade Unternehmensdaten…" />
      </SettingsCard>
    );
  }

  if (loadError) {
    return (
      <SettingsCard title="Zahlungsziel & Skonto">
        <p className="text-sm text-red-700">{loadError}</p>
      </SettingsCard>
    );
  }

  const previewBlock = (
    <div className="space-y-3 border-t border-app-border pt-5">
      {showInvoices ? (
        <div>
          <p className={fieldLabel}>Vorschau in Rechnungen</p>
          <p className="text-sm text-app-text-muted mt-1.5">{invoicePreview}</p>
        </div>
      ) : null}
      {showOffers ? (
        <div>
          <p className={fieldLabel}>Vorschau in Angeboten</p>
          <p className="text-sm text-app-text-muted mt-1.5">{offerPreview}</p>
        </div>
      ) : null}
    </div>
  );

  return (
    <SettingsCard
      title="Zahlungsziel & Skonto"
      actions={
        <>
          <SettingsCardSavedStatus saved={saved && !editing} />
          {editing ? (
            <>
              <button type="button" className={btnOutline} onClick={cancelEdit} disabled={saving}>
                Abbrechen
              </button>
              <button type="button" className={btnPrimary} onClick={() => void save()} disabled={saving || !dirty}>
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
            </>
          ) : (
            <button type="button" className={btnOutline} onClick={beginEdit}>
              Bearbeiten
            </button>
          )}
        </>
      }
    >
        {error ? (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
        ) : null}
        {editing ? (
          <div className="space-y-5 max-w-md">
            <div>
              <label className={`block ${fieldLabel} mb-1.5`} htmlFor="zahlungsziel-select">
                Zahlungsziel
              </label>
              <AppSelect
                id="zahlungsziel-select"
                value={selectValue}
                onChange={onSelectChange}
                options={ZAHLUNGSZIEL_OPTIONS}
                className="w-full"
                triggerClassName={selectTriggerClass}
              />
              {selectValue === 'custom' ? (
                <div className="mt-3">
                  <label className={`block ${fieldLabel} mb-1.5`} htmlFor="zahlungsziel-days">
                    Tage
                  </label>
                  <input
                    id="zahlungsziel-days"
                    type="number"
                    min={1}
                    max={9999}
                    step={1}
                    value={paymentTermsDraft}
                    onChange={(e) => {
                      setPaymentTermsDraft(e.target.value);
                      const n = parseInt(e.target.value, 10);
                      if (Number.isFinite(n) && n >= 1) {
                        setForm((f) => ({ ...f, payment_terms_days: Math.min(9999, n) }));
                      }
                    }}
                    className={inputClass}
                  />
                </div>
              ) : null}
              <p className="text-xs text-app-text-subtle mt-1.5">
                {form.payment_terms_mode === 'immediate'
                  ? `Sofort ist ohne Abzug. In der Rechnungsübersicht fällig nach ${IMMEDIATE_DUE_WORKING_DAYS} Werktagen.`
                  : 'Frist bis zur fälligen Zahlung der Rechnung.'}
              </p>
            </div>
            <div>
              <label
                className={`flex items-center gap-2 ${form.payment_terms_mode === 'immediate' ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${fieldLabel} mb-2`}
              >
                <input
                  type="checkbox"
                  className="rounded border-app-border-strong text-pipedrive-blue focus:ring-pipedrive-blue/30"
                  checked={form.skonto_percent != null}
                  disabled={form.payment_terms_mode === 'immediate'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const nextPercent = form.skonto_percent ?? 2;
                      const nextDays = form.skonto_days ?? DEFAULT_SKONTO_DAYS;
                      setForm((f) => ({ ...f, skonto_percent: nextPercent, skonto_days: nextDays }));
                      setSkontoDraft((prev) => prev || skontoDraftFromPercent(nextPercent));
                      setSkontoDaysDraft((prev) => prev || String(nextDays));
                    } else {
                      setForm((f) => ({ ...f, skonto_percent: null, skonto_days: null }));
                      setSkontoDraft('');
                      setSkontoDaysDraft('');
                    }
                  }}
                />
                <span className={fieldLabel}>
                  Skonto gewähren
                </span>
              </label>
              {form.payment_terms_mode === 'immediate' ? (
                <p className="text-xs text-app-text-subtle mb-2">Sofort gilt ohne Abzug — Skonto ist hier deaktiviert.</p>
              ) : null}
              {form.payment_terms_mode !== 'immediate' && form.skonto_percent != null ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`block ${fieldLabel} mb-1.5`} htmlFor="skonto-percent">
                      Skonto (%)
                    </label>
                    <input
                      id="skonto-percent"
                      type="text"
                      inputMode="decimal"
                      value={skontoDraft}
                      onChange={(e) => setSkontoDraft(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={`block ${fieldLabel} mb-1.5`} htmlFor="skonto-days">
                      Skonto (Tage)
                    </label>
                    <input
                      id="skonto-days"
                      type="number"
                      min={1}
                      max={9999}
                      step={1}
                      value={skontoDaysDraft}
                      onChange={(e) => {
                        setSkontoDaysDraft(e.target.value);
                        const n = parseSkontoDays(e.target.value);
                        if (n != null) setForm((f) => ({ ...f, skonto_days: n }));
                      }}
                      className={inputClass}
                    />
                  </div>
                </div>
              ) : null}
            </div>
            {previewBlock}
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <p className={fieldLabel}>Zahlungsziel</p>
              <p className="text-sm text-app-text mt-1.5">
                {formatPaymentTermsDisplay(form.payment_terms_mode, form.payment_terms_days)}
              </p>
            </div>
            <div>
              <p className={fieldLabel}>Skonto</p>
              <p className="text-sm text-app-text mt-1.5">
                {form.payment_terms_mode === 'immediate'
                  ? 'Kein Skonto (Sofort ohne Abzug)'
                  : formatSkontoDisplay(form.skonto_percent, form.skonto_days)}
              </p>
            </div>
            {previewBlock}
          </div>
        )}
    </SettingsCard>
  );
}
